'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';
import TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import CheckCircle from '@alltrails/denali/icons/CheckCircle';
import CheckCircleFilled from '@alltrails/denali/icons/CheckCircleFilled';
import Download from '@alltrails/denali/icons/Download';
import Share from '@alltrails/denali/icons/Share';
import VerifiedFilled from '@alltrails/denali/icons/VerifiedFilled';
import CreateEventIconButton from '@alltrails/events/components/CreateEventIconButton';
import useNavigateBackInternal from '@alltrails/layout/hooks/useNavigateBackInternal';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useTrailCompleted from '@alltrails/lists/hooks/useTrailCompleted';
import useSharing from '@alltrails/sharing/hooks/useSharing';
import type { TrailPage } from '@alltrails/trails/types/trailPage';
import getTrailShareObject from '@alltrails/trails/utils/getTrailShareObject';
import type TrailDetails from '@alltrails/types/TrailDetails';
import CreateInviteCallout from '@/components/CreateInviteCallout/CreateInviteCallout';
import SaveCta from '@/components/SaveCta';
import getTrailPageType from '@/utils/getTrailPageType';
import styles from './MobileCtaBar.module.scss';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const VerifiedCompletedDialog = dynamic(() => import('../VerifiedCompletedDialog/VerifiedCompletedDialog'), { ssr: false });

const messages = defineMessages({
  ADD_TO_COMPLETED: { defaultMessage: 'Add to completed' },
  BACK: { defaultMessage: 'Back' },
  BACK_TO_EXPLORE: { defaultMessage: ' Back to Explore' },
  DOWNLOAD: { defaultMessage: 'Download' },
  REMOVE_FROM_COMPLETED: { defaultMessage: 'Remove from completed' },
  SHARE: { defaultMessage: 'Share' },
  VERIFIED: { defaultMessage: 'Verified completed' }
});

type MobileCtaBarProps = {
  className?: string;
  elevated?: ComponentProps<typeof IconButton>['elevated'];
  name: string;
  showInviteCallout?: boolean;
  slug: string;
  trailDetails: TrailDetails;
  trailId: number;
  trailPage: TrailPage;
  variant?: ComponentProps<typeof IconButton>['variant'];
};

const MobileCtaBar = ({
  className,
  elevated = true,
  name,
  showInviteCallout = false,
  slug,
  trailDetails,
  trailId,
  trailPage,
  variant = 'ghost'
}: MobileCtaBarProps) => {
  const {
    formattedDefaultMessages: { ADD_TO_COMPLETED, BACK, BACK_TO_EXPLORE, DOWNLOAD, REMOVE_FROM_COMPLETED, SHARE, VERIFIED }
  } = useFormatMessage(messages);

  const isMobile = useIsMobileSizedScreen();
  const user = useUser();
  const { canNavigateBack, navigateBack } = useNavigateBackInternal();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [isVerifiedDialogOpen, setIsVerifiedDialogOpen] = useState(false);

  const isBaseUser = user && !isSubscriber(user);

  const { isVerified } = useReduxListItems();
  const { completed, toggleTrailCompleted } = useTrailCompleted(trailId);
  const verified = isVerified('trail', trailId);

  const trailPageType = getTrailPageType(trailDetails);

  const onAddToCompleteClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: completed ? 'remove from completed' : 'add to completed',
      trail_page_location: 'hero image inset',
      trail_page_type: trailPageType
    });
    if (user) {
      toggleTrailCompleted();
    } else {
      setIsSignupModalOpen(true);
    }
  }, [completed, trailId, user, toggleTrailCompleted, trailPageType]);

  const onVerifyClick = () => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'add to completed',
      trail_page_location: 'hero image inset',
      trail_page_type: trailPageType
    });
    setIsVerifiedDialogOpen(true);
  };

  const onShareOptionClick = useCallback(
    (shareOption: TrailDetailsShareOption) => {
      logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailId.toString() });
    },
    [trailId]
  );

  const shareObject = useMemo(() => getTrailShareObject({ id: trailId, slug, name } as TrailDetails), [name, slug, trailId]);

  const { isShareModalOpen, openShare, shareModalProps } = useSharing({ shareObject, trailPageType });

  const onShareClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'share',
      trail_page_location: 'hero image inset',
      trail_page_type: trailPageType
    });

    openShare();
  }, [openShare, trailId, trailPageType]);

  return (
    <div className={classNames(styles.ctaBar, className)}>
      <IconButton
        className={classNames(styles.backToExplore)}
        elevated={elevated}
        icon={{ Component: ArrowSq, orientation: 'left' }}
        onClick={navigateBack}
        size="sm"
        title={canNavigateBack && isMobile ? BACK : BACK_TO_EXPLORE}
        variant={variant}
      />
      {!user ? (
        <IconButton
          elevated={elevated}
          icon={{ Component: Download }}
          onClick={() => {
            logTrailDetailsActionTapped({
              trail_id: trailId.toString(),
              trail_page_action: 'download overflow menu button',
              trail_page_location: 'hero image inset',
              trail_page_type: trailPageType
            });
            setIsDownloadModalOpen(true);
          }}
          size="sm"
          title={DOWNLOAD}
          variant={variant}
        />
      ) : (
        <IconButton elevated={elevated} icon={{ Component: Share }} onClick={onShareClick} size="sm" title={SHARE} variant={variant} />
      )}
      <CreateInviteCallout
        anchor={
          <CreateEventIconButton
            elevated={elevated}
            entryPoint="mobile web trail details cta bar"
            size="sm"
            mapId={trailDetails.id}
            mapType="T"
            variant={variant}
          />
        }
        isEnabled={showInviteCallout}
      />
      {verified ? (
        <IconButton elevated={elevated} icon={{ Component: VerifiedFilled }} onClick={onVerifyClick} size="sm" title={VERIFIED} variant={variant} />
      ) : (
        <IconButton
          elevated={elevated}
          icon={{ Component: completed ? CheckCircleFilled : CheckCircle }}
          onClick={onAddToCompleteClick}
          size="sm"
          title={completed ? REMOVE_FROM_COMPLETED : ADD_TO_COMPLETED}
          variant={variant}
        />
      )}
      {isBaseUser ? (
        <IconButton
          elevated={elevated}
          icon={{ Component: Download }}
          onClick={() => {
            logTrailDetailsActionTapped({
              trail_id: trailId.toString(),
              trail_page_action: 'download overflow menu button',
              trail_page_location: 'hero image inset',
              trail_page_type: trailPageType
            });
            setIsDownloadModalOpen(true);
          }}
          size="sm"
          title={DOWNLOAD}
          variant={variant}
        />
      ) : (
        <SaveCta
          buttonLocation="hero image inset"
          elevated={elevated}
          size="sm"
          trailDetails={trailDetails}
          trailId={trailId}
          trailPageType={trailPageType}
          variant={variant}
        />
      )}
      {isDownloadModalOpen && (
        <HitTheTrailModal
          isOpen={isDownloadModalOpen}
          onCloseRequest={() => setIsDownloadModalOpen(false)}
          title={DOWNLOAD}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
      {isShareModalOpen && <ShareModal {...shareModalProps} onShareOptionClick={onShareOptionClick} />}
      {isVerifiedDialogOpen && <VerifiedCompletedDialog onClose={() => setIsVerifiedDialogOpen(false)} />}
      {isSignupModalOpen && (
        <SignUpModal
          isOpen={isSignupModalOpen}
          closeModal={() => setIsSignupModalOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => {
            setIsSignupModalOpen(false);
            toggleTrailCompleted();
          }}
          trigger="Add Trail to Completed List"
          upsellTrigger="Add Trail to Completed List"
        />
      )}
    </div>
  );
};

export default MobileCtaBar;
